type AsyncFunction<Params extends unknown[]> = (...params: Params) => Promise<unknown>;

/**
 * Helper class that enables "debounced" execution of an async function, also
 * ensuring that no more than one execution of that function is running at the same time.
 */
export default class AsyncDebouncer<Params extends unknown[]> {
  private readonly execute: AsyncFunction<Params>;

  private readonly debounceDelay: number;

  private readonly maxWait: number;

  private current: Promise<unknown> | null = null;

  private pending: Params | null = null;

  private flushed: boolean = false;

  private debounceTimeout: ReturnType<typeof setTimeout> | null = null;

  private maxWaitTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor({
    execute,
    debounceDelay,
    maxWait,
  }: {
    execute: AsyncFunction<Params>;
    debounceDelay: number;
    maxWait: number;
  }) {
    this.execute = execute;
    this.debounceDelay = debounceDelay;
    this.maxWait = maxWait;
  }

  /**
   * Schedules an async function execution to be run
   * when possible.
   */
  public schedule(...params: Params): void {
    this.pending = params;
    if (this.current == null) {
      this.debounce();
    }
  }

  /**
   * Enforces sending any pending execution immediately
   * (or right after any on-going execution)
   */
  public flush(): void {
    if (this.pending != null) {
      this.flushed = true;
      if (this.current == null) {
        this.processNext();
      }
    }
  }

  /**
   * Resets debounce timer and ensures that max wait timer is set.
   */
  private debounce() {
    this.clearDebounceTimeout();
    this.debounceTimeout = setTimeout(this.processNext, this.debounceDelay);
    this.maxWaitTimeout ??= setTimeout(this.processNext, this.maxWait);
  }

  /**
   * Starts processing any pending execution
   */
  private readonly processNext = () => {
    this.clearDebounceTimeout();
    this.clearMaxWaitTimeout();
    this.flushed = false;
    const { pending } = this;
    if (pending != null) {
      this.pending = null;
      this.current = this.startExecution(...pending);
    }
  };

  /**
   * Starts execution with the given params, with the post-process
   * cleanups and checks.
   */
  private async startExecution(...params: Params): Promise<void> {
    try {
      await this.execute(...params);
    } finally {
      this.current = null;
      if (this.flushed) {
        this.processNext();
      } else if (this.pending != null) {
        this.debounce();
      }
    }
  }

  private clearDebounceTimeout() {
    const { debounceTimeout } = this;
    if (debounceTimeout != null) {
      clearTimeout(debounceTimeout);
    }
    this.debounceTimeout = null;
  }

  private clearMaxWaitTimeout() {
    const { maxWaitTimeout } = this;
    if (maxWaitTimeout != null) {
      clearTimeout(maxWaitTimeout);
    }
    this.maxWaitTimeout = null;
  }
}
